import { useState, useCallback } from 'react';
import { Form, message } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useAppSelector } from '../../../redux/hooks';
import {
  createExpenseHeader,
  updateExpenseHeader,
  updateExpenseRows,
  sendExpenseHeader,
  getProfileByUserId,
} from '../../../api/api';
import { UseExpenseFormProps, ExpenseRowItem } from '../types';

/**
 * Custom React hook for expense form state management and operations
 *
 * Manages the complete lifecycle of expense forms including:
 * - Form state initialization and validation
 * - CRUD operations for expense headers and rows
 * - Draft saving and submission workflows
 * - File upload coordination
 * - Multi-user expense creation (admin functionality)
 * - Error handling and user feedback
 *
 * Business Logic:
 * - Handles both create and edit modes
 * - Supports private card vs company card expenses
 * - Manages owner assignment for admin users
 * - Coordinates with backend API for persistence
 *
 * @param props - UseExpenseFormProps containing form configuration
 * @returns Object containing form instance, handlers, and state flags
 */
export const useExpenseForm = ({
  clientId,
  profile,
  expenseSettings,
  recordToEdit,
  isMyExpense,
  isPrivateCard,
  updateRecordToEdit,
}: UseExpenseFormProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  const [submitting, setSubmitting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [formChanged, setFormChanged] = useState(false);
  const [headerId, setHeaderId] = useState(recordToEdit?.id ?? '');
  const [status, setStatus] = useState<string>(recordToEdit?.status ?? '');
  const [isFormInitialized, setIsFormInitialized] = useState(false);

  // Fetch owner profile when needed
  const fetchOwnerProfile = useCallback(async () => {
    try {
      const ownerEmail = form.getFieldValue('owner');
      if (!ownerEmail) {
        form.setFields([
          {
            name: 'owner',
            value: form.getFieldValue('owner'),
            errors: [t('Owner does not have a profile!')],
          },
        ]);
        return null;
      }
      const response = await getProfileByUserId(accessToken, ownerEmail);
      return response.data;
    } catch (error) {
      if (form.getFieldValue('owner') !== '') {
        form.setFields([
          {
            name: 'owner',
            value: form.getFieldValue('owner'),
            errors: [t('Owner does not have a profile!')],
          },
        ]);
      }
      console.error('Error fetching owner ID:', error);
      return null;
    }
  }, [accessToken, form, t]);

  // Create new expense
  const createExpense = useCallback(
    async (uploadFiles: (id?: string) => void) => {
      setSubmitting(true);
      try {
        await form.validateFields();
        const expenseHeaderDate = dayjs(form.getFieldValue('date')).format(
          'YYYY-MM-DD'
        );
        const owner: API.Profile = isMyExpense
          ? profile
          : (await fetchOwnerProfile());
        const expensesPayload = form
          .getFieldValue('expenses')
          .map((item: ExpenseRowItem) => ({
            ...item,
            title: expenseSettings.multiple_rows
              ? item.title
              : form.getFieldValue('purpose'),
            date: expenseSettings.multiple_rows
              ? dayjs(item.date).format('YYYY-MM-DD')
              : expenseHeaderDate,
            amount: item.nok + '.' + item.ore,
          }));

        const dataToSend: API.expenseHeaderParams = {
          status: 'draft',
          date: expenseHeaderDate,
          purpose: form.getFieldValue('purpose'),
          external: isPrivateCard,
          user_id: profile.user_id,
          dimension_id: form.getFieldValue('dimension'),
          project_id: form.getFieldValue('project'),
          xgl_id: form.getFieldValue('xgl'),
          expense_rows: expensesPayload,
          client_id: clientId,
          company_card_id: form.getFieldValue('card'),
          owner_id: owner.id!,
          submitter_id: profile.id,
        };

        const newExpenseHeader = await createExpenseHeader(
          accessToken,
          dataToSend
        );
        setHeaderId(newExpenseHeader.data.id);

        const returnedExpenseRows = newExpenseHeader.data.expense_rows.map(
          (item: ExpenseRowItem) => {
            const [nok, ore] = item.amount.toString().split('.');
            return {
              ...item,
              id: item.id,
              date: dayjs(item.date),
              nok: nok,
              ore: ore?.length === 1 ? `${ore}0` : ore || '00',
            };
          }
        );

        form.setFieldValue('expenses', returnedExpenseRows);
        
        // Update recordToEdit with the newly created expense data
        if (updateRecordToEdit) {
          updateRecordToEdit({ ...newExpenseHeader.data, owner: owner});
        }
        
        uploadFiles(newExpenseHeader.data.id);
        setStatus('draft');
        message.success(t('Expense created!'));

        return newExpenseHeader.data;
      } catch (error) {
        console.error('Error creating expense:', error);
        message.error(t('Failed to create expense. Please try again.'));
        throw error;
      } finally {
        setSubmitting(false);
        setFormChanged(false);
      }
    },
    [
      form,
      isMyExpense,
      profile,
      fetchOwnerProfile,
      expenseSettings,
      isPrivateCard,
      clientId,
      accessToken,
      updateRecordToEdit,
      t,
    ]
  );

  // Update existing expense
  const updateExpense = useCallback(
    async (uploadFiles: () => void) => {
      setFormChanged(false);
      setSubmitting(true);
      try {
        await form.validateFields();
        const owner: API.ProfileMe = isMyExpense
          ? profile
          : (await fetchOwnerProfile());

          const dataToSend: API.expenseHeaderParams = {
          status: 'draft',
          date: dayjs(form.getFieldValue('date')).format('YYYY-MM-DD'),
          purpose: form.getFieldValue('purpose'),
          external: isPrivateCard,
          user_id: profile.user_id,
          dimension_id: form.getFieldValue('dimension'),
          project_id: form.getFieldValue('project'),
          xgl_id: form.getFieldValue('xgl'),
          client_id: clientId,
          company_card_id: form.getFieldValue('card'),
          admins: recordToEdit?.admins,
          users: recordToEdit?.users,
          owner_id: owner.id!,
          submitter_id: profile.id,
        };

        // Save header
        const updatedExpenseHeader = await updateExpenseHeader(
          accessToken,
          headerId,
          dataToSend
        );

        // Save rows
        const expensesPayload = form
          .getFieldValue('expenses')
          .map((item: ExpenseRowItem) => ({
            ...item,
            id: item.id,
            date: dayjs(item.date).format('YYYY-MM-DD'),
            amount: item.nok + '.' + item.ore,
          }));

        await updateExpenseRows(accessToken, headerId, expensesPayload);
        
        // Update recordToEdit with the updated expense data
        if (updateRecordToEdit) {
          updateRecordToEdit({...updatedExpenseHeader.data, owner: owner});
        }
        
        uploadFiles();
        message.success(t('Expense saved!'));
        
        return updatedExpenseHeader.data;
      } catch (error) {
        console.error('Error updating expense:', error);
        message.error(t('Failed to save expense. Please try again.'));
        throw error;
      } finally {
        setSubmitting(false);
        setFormChanged(false);
      }
    },
    [
      form,
      isMyExpense,
      profile,
      fetchOwnerProfile,
      isPrivateCard,
      clientId,
      recordToEdit,
      accessToken,
      headerId,
      updateRecordToEdit,
      t,
    ]
  );

  // Approve expense
  const approveExpense = useCallback(
    async (files: any[], uploadFiles: () => void) => {
      setApproving(true);
      try {
        await form.validateFields();
        if (headerId !== '') {
          const expenses = form.getFieldValue('expenses') || [];
          // Check if all rows have attachments
          const allRowsHaveAttachments = expenses.every(
            (expense: ExpenseRowItem) => {
              const correspondingFile = files.find(
                (file) => file.id === expense.id
              );
              return (
                correspondingFile &&
                (correspondingFile.file ||
                  (correspondingFile.fileList &&
                    correspondingFile.fileList.length > 0))
              );
            }
          );
          if (!allRowsHaveAttachments) {
            message.error(
              t('Please attach files to all expense rows before submitting.')
            );
            return;
          }
          setSubmitting(true);
          await updateExpense(uploadFiles);
          await sendExpenseHeader(accessToken, headerId);
          message.success(t('Expense aproved!'));
          setStatus('sent');
        }
      } catch (error) {
        console.error('Error approving expense:', error);
        message.error(t('Failed to approve expense. Please try again.'));
        throw error;
      } finally {
        setApproving(false);
        setSubmitting(false);
      }
    },
    [form, headerId, updateExpense, accessToken, t]
  );

  // Set initial form values - critical for editing/viewing existing expenses
  // Only runs once to prevent overriding user changes
  const setInitialForm = useCallback(() => {
    // Only initialize if not already initialized
    if (!isFormInitialized) {
      if (status !== '' && recordToEdit) {
        // Populate form with existing expense data
        form.setFieldValue('external', recordToEdit.external);
        form.setFieldValue('card', recordToEdit.company_card_id);
        form.setFieldValue('purpose', recordToEdit.purpose);
        form.setFieldValue('date', dayjs(recordToEdit.date));
        form.setFieldValue('dimension', recordToEdit.dimension_id);
        form.setFieldValue('project', recordToEdit.project_id);
        form.setFieldValue('xgl', recordToEdit.xgl_id);
        form.setFieldValue(
          'owner',
          isMyExpense ? profile.user_id : (recordToEdit.owner?.user_id ?? recordToEdit.owner_id ?? '')
        );
        console.log('Setting initial form values for expense:', recordToEdit.id);
      } else {
        // Set defaults for new expense
        form.setFieldValue('external', isPrivateCard);
        form.setFieldValue('owner', isMyExpense ? profile.user_id : '');
        form.setFieldValue('date', dayjs());
        console.log('Setting initial form values for new expense');
      }
      
      // Mark form as initialized
      setIsFormInitialized(true);
    }
  }, [status, recordToEdit, isMyExpense, profile.user_id, form, isPrivateCard, isFormInitialized]);

  return {
    // Form instance
    form,

    // State
    submitting,
    approving,
    formChanged,
    headerId,
    status,

    // Actions
    createExpense,
    updateExpense,
    approveExpense,
    setInitialForm,

    // Setters
    setApproving,
    setFormChanged,
  };
};
